import { useState } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Pencil, Plus, Trash2, LogOut, ExternalLink, Users, Eye, Download, Image as ImageIcon } from "lucide-react";
import { useApi, api, fmtDate, catLabel } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { StatusBadge, articleState } from "./status";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";

const Row = ({ a, onDelete, i }) => (
  <div className="grid grid-cols-[64px_1fr_auto] sm:grid-cols-[80px_1fr_120px_auto] gap-4 items-center py-4 border-b border-ink/15" data-testid={`admin-row-${i}`}>
    <img src={a.cover} alt="" className="h-16 w-16 sm:h-20 sm:w-20 object-cover" />
    <div className="min-w-0">
      <div className="flex flex-wrap items-center gap-2">
        <p className="kicker text-rose">{catLabel(a.category)}</p>
        <StatusBadge a={a} testId={`admin-status-${i}`} />
      </div>
      <p className="display text-xl sm:text-2xl leading-tight truncate mt-1">{a.title}</p>
      <p className="kicker text-ink/60 mt-1">{a.author} · {fmtDate(a.published)}</p>
    </div>
    <span className="hidden sm:block kicker text-ink/60">{a.views?.toLocaleString?.() ?? 0} reads</span>
    <div className="flex gap-2">
      {articleState(a) === "live" ? (
        <Link to={`/article/${a.slug}`} target="_blank" className="admin-icon-btn" aria-label="View" data-testid={`admin-view-${i}`}><ExternalLink className="h-4 w-4" /></Link>
      ) : (
        <Link to={`/admin/preview/${a.slug}`} className="admin-icon-btn" aria-label="Preview" data-testid={`admin-preview-${i}`}><Eye className="h-4 w-4" /></Link>
      )}
      <Link to={`/admin/edit/${a.slug}`} className="admin-icon-btn" aria-label="Edit" data-testid={`admin-edit-${i}`}><Pencil className="h-4 w-4" /></Link>
      <button onClick={() => onDelete(a)} className="admin-icon-btn hover:!bg-rose" aria-label="Delete" data-testid={`admin-delete-${i}`}><Trash2 className="h-4 w-4" /></button>
    </div>
  </div>
);

export default function AdminList() {
  const { user, logout } = useAuth();
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [target, setTarget] = useState(null);
  const [tab, setTab] = useState("all");
  const { data, isLoading } = useApi("/admin/articles", { q });
  const all = Array.isArray(data?.items) ? data.items : [];
  const items = tab === "all" ? all : all.filter((a) => articleState(a) === tab);
  const count = (t) => all.filter((a) => articleState(a) === t).length;

  const remove = async () => {
    try {
      await api.delete(`/admin/articles/${target.slug}`);
      toast.success("Article deleted");
      qc.invalidateQueries();
    } catch {
      toast.error("Could not delete the article");
    }
    setTarget(null);
  };

  return (
    <main className="pt-28 pb-24 max-w-[1200px] mx-auto px-5 sm:px-10" data-testid="admin-list-page">
      <div className="flex flex-wrap items-end justify-between gap-4 border-b-2 border-ink pb-6">
        <div>
          <p className="kicker text-rose">Newsroom · {user?.email}</p>
          <h1 className="display text-6xl sm:text-7xl mt-2">Articles <span className="text-pink">{data?.total ?? ""}</span></h1>
        </div>
        <div className="flex gap-2">
          <Link to="/admin/new" className="inline-flex items-center gap-2 bg-hot text-ink kicker px-5 py-3 border-2 border-ink hover:bg-ink hover:text-cream transition-colors" data-testid="admin-new-btn">
            <Plus className="h-4 w-4" /> New article
          </Link>
          <Link to="/admin/banner" className="inline-flex items-center gap-2 kicker px-4 py-3 border-2 border-ink hover:bg-ink hover:text-cream transition-colors" data-testid="admin-banner-link">
            <ImageIcon className="h-4 w-4" /> Banner
          </Link>
          <Link to="/admin/issue" className="inline-flex items-center gap-2 kicker px-4 py-3 border-2 border-ink hover:bg-ink hover:text-cream transition-colors" data-testid="admin-issue-link">
            <Download className="h-4 w-4" /> Print issue
          </Link>
          {user?.role === "admin" && (
            <Link to="/admin/import" className="inline-flex items-center gap-2 kicker px-4 py-3 border-2 border-ink hover:bg-ink hover:text-cream transition-colors" data-testid="admin-import-link">
              <Download className="h-4 w-4" /> Import
            </Link>
          )}
          {user?.role === "admin" && (
            <Link to="/admin/team" className="inline-flex items-center gap-2 kicker px-4 py-3 border-2 border-ink hover:bg-ink hover:text-cream transition-colors" data-testid="admin-team-link">
              <Users className="h-4 w-4" /> Team
            </Link>
          )}
          <button onClick={logout} className="inline-flex items-center gap-2 kicker px-4 py-3 border-2 border-ink hover:bg-ink hover:text-cream transition-colors" data-testid="admin-logout-btn">
            <LogOut className="h-4 w-4" /> Log out
          </button>
        </div>
      </div>
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by title…" className="admin-input mt-8" data-testid="admin-search-input" />
      <div className="flex flex-wrap gap-2 mt-4" data-testid="admin-status-tabs">
        {["all", "live", "scheduled", "draft"].map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`kicker px-4 py-2 border-2 border-ink transition-colors ${tab === t ? "bg-ink text-cream" : "hover:bg-blush"}`}
            data-testid={`admin-tab-${t}`}
          >
            {t === "draft" ? "drafts" : t} {t === "all" ? all.length : count(t)}
          </button>
        ))}
      </div>
      {isLoading && <p className="kicker mt-8">Loading…</p>}
      <div className="mt-4">
        {items.map((a, i) => <Row key={a.slug} a={a} i={i} onDelete={setTarget} />)}
      </div>
      <AlertDialog open={!!target} onOpenChange={(o) => !o && setTarget(null)}>
        <AlertDialogContent data-testid="admin-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this article?</AlertDialogTitle>
            <AlertDialogDescription>“{target?.title}” will be removed from the site. This can’t be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} data-testid="admin-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </main>
  );
}
